/**
 * Action Button Helpers
 * Utility functions to create consistent action button configurations
 */

/**
 * Creates a button visibility function
 * @param {Function} condition - Function that takes an item and returns boolean
 * @returns {Function} - Show function for the button
 */
export const createShowFunction = (condition) => {
  return (item) => {
    try {
      return condition(item);
    } catch (error) {
      console.warn("Error in button visibility condition:", error);
      return false; // Default to hide if error
    }
  };
};

/**
 * Common visibility conditions
 */
export const commonConditions = {
  // Always show
  always: () => true,
  
  // Never show
  never: () => false,
  
  // Show for active items (status = 1)
  activeOnly: (item) => item.status?.data === 1,

    // Show for active items (status = 1)
  inactiveOnly: (item) => item.status?.data === 0,
  
  // Show for inactive items (status = 0)
  pendingOnly: (item) => item.status?.data === 3,
  
  // Show for non-system generated items
  nonSystemGenerated: (item) => item.system_generated === 0,
  
  // Show for system generated items
  systemGenerated: (item) => item.system_generated === 1,
  
  // Show if item has files
  hasFiles: (item) => item.files && item.files.length > 0,
  
  // Show if item can be edited (not system generated and active)
  canEdit: (item) => item.system_generated?item.system_generated === 0:true && item.status?.data === 1,
  
  // Show if item can be deleted (not system generated)
  canDelete: (item) => item.system_generated?item.system_generated === 0:true,
};

/**
 * Creates action button configuration with visibility controls
 * @param {Object} config - Configuration object
 * @param {Object} options - Additional options for customizing behavior
 * @returns {Object} - Complete action button configuration
 * 
 * Example usage:
 * const actionButtons = createActionButtons({
 *   delete: {
 *     handler: (item) => handleDelete(item.id),
 *     show: commonConditions.canDelete
 *   },
 *   edit: {
 *     handler: (item) => handleEdit(item),
 *     show: commonConditions.canEdit
 *   },
 *   view: {
 *     handler: (item) => handleView(item),
 *     show: commonConditions.always
 *   }
 * }, {
 *   systemGeneratedBadge: true, // Enable system generated badge
 *   badgeText: "System Generated", // Custom badge text
 *   badgeVariant: "info" // Badge variant (info, warning, success, etc.)
 * });
 */
export const createActionButtons = (config, options = {}) => {
  const actionButtons = {};
  
  // Check if system generated badge is enabled
  const enableSystemGeneratedBadge = options.systemGeneratedBadge !== false;
  
  Object.keys(config).forEach(buttonKey => {
    const { handler, show } = config[buttonKey];
    
    // Add the action handler
    if (handler) {
      actionButtons[buttonKey] = handler;
    }
    
    // Add the visibility function
    if (show) {
      const showFunctionKey = `show${buttonKey.charAt(0).toUpperCase() + buttonKey.slice(1)}`;
      actionButtons[showFunctionKey] = createShowFunction(show);
    }
  });
  
  // Add system generated badge functionality
  if (enableSystemGeneratedBadge) {
    actionButtons.isSystemGenerated = (item) => item.system_generated === 1;
    actionButtons.systemGeneratedBadge = {
      text: options.badgeText || "System Generated",
      variant: options.badgeVariant || "info",
      className: "px-2 py-1 text-xs font-medium rounded-full"
    };
  }
  
  return actionButtons;
};

/**
 * Creates a custom condition function
 * @param {Array} conditions - Array of condition functions
 * @param {string} operator - 'AND' or 'OR' (default: 'AND')
 * @returns {Function} - Combined condition function
 * 
 * Example:
 * const customCondition = createCustomCondition([
 *   commonConditions.activeOnly,
 *   commonConditions.nonSystemGenerated
 * ], 'AND');
 */
export const createCustomCondition = (conditions, operator = 'AND') => {
  return (item) => {
    if (operator === 'OR') {
      return conditions.some(condition => condition(item));
    } else {
      return conditions.every(condition => condition(item));
    }
  };
};

/**
 * ========================================
 * createProtectedActionButtons
 * ========================================
 * 
 * Creates action buttons WITH automatic permission checking.
 * This is the recommended way to create action buttons with permissions.
 * 
 * @param {Object} config - Action button configuration
 * @param {Object} permissions - Permission object from usePermissionCheck hook
 * @param {Object} options - Additional options
 * @returns {Object} - Complete action button configuration with permission checks
 * 
 * USAGE EXAMPLE:
 * ==============
 * 
 * import { usePermissionCheck } from '../../../../hooks/usePermissionCheck';
 * import { createProtectedActionButtons } from '../../../../utils/actionButtonHelpers';
 * 
 * const MyPage = () => {
 *   const permissions = usePermissionCheck('configuration', 'department');
 *   
 *   const actionButtons = createProtectedActionButtons({
 *     edit: {
 *       handler: (item) => handleEdit(item),
 *       requirePermission: 'update', // Will check canUpdate permission
 *       condition: commonConditions.activeOnly // Optional additional condition
 *     },
 *     delete: {
 *       handler: (item) => handleDelete(item),
 *       requirePermission: 'delete', // Will check canDelete permission
 *       condition: commonConditions.canDelete
 *     },
 *     active: {
 *       handler: (item) => handleActivate(item),
 *       requirePermission: 'update',
 *       condition: commonConditions.inactiveOnly
 *     },
 *     inactive: {
 *       handler: (item) => handleDeactivate(item),
 *       requirePermission: 'update',
 *       condition: commonConditions.activeOnly
 *     }
 *   }, permissions);
 * };
 */
export const createProtectedActionButtons = (config, permissions, options = {}) => {
  const actionButtons = {};
  
  // Safety check: if permissions is not provided or invalid, deny all permissions
  if (!permissions || typeof permissions !== 'object') {
    console.warn('[createProtectedActionButtons] Permissions object is missing or invalid. All buttons will be hidden.');
    // Return empty action buttons with show functions that always return false
    Object.keys(config).forEach(buttonKey => {
      const showFunctionKey = `show${buttonKey.charAt(0).toUpperCase() + buttonKey.slice(1)}`;
      actionButtons[showFunctionKey] = createShowFunction(() => false);
    });
    return actionButtons;
  }
  
  // Map permission requirement to permission check
  const permissionMap = {
    'create': permissions.canCreate || false,
    'update': permissions.canUpdate || false,
    'delete': permissions.canDelete || false,
    'view': permissions.canView || false,
  };  
  
  Object.keys(config).forEach(buttonKey => {
    const { handler, requirePermission, condition } = config[buttonKey];
    
    // Create show function with permission check FIRST
    const showFunctionKey = `show${buttonKey.charAt(0).toUpperCase() + buttonKey.slice(1)}`;
    actionButtons[showFunctionKey] = createShowFunction((item) => {
      // Check permission first - if no permission, hide button immediately
      if (requirePermission) {
        const hasPermission = permissionMap[requirePermission];
        // CRITICAL: If permission is false, undefined, or null, hide the button
        if (!hasPermission) {
          return false; // Hide button if no permission
        }
      }
     
      // Then check additional condition if provided
      if (condition) {
        const conditionResult = condition(item);
        return conditionResult;
      }
      // If no condition, just check permission (already checked above)
      return true;
    });
    
    // Only add the action handler if permission exists (optional optimization)
    // But we still create the handler so the table component can check the show function
    if (handler) {
      actionButtons[buttonKey] = handler;
    }
  });
  
  return actionButtons;
};

/**
 * Predefined button configurations for common use cases
 */
export const presetConfigurations = {
  // Standard CRUD operations
  crud: {
    view: { show: commonConditions.always },
    edit: { show: commonConditions.canEdit },
    delete: { show: commonConditions.canDelete }
  },
  
  // Department management specific
  department: {
    edit: { show: commonConditions.always },
    delete: { show: commonConditions.nonSystemGenerated },
    view: { show: commonConditions.activeOnly }
  },
  
  // User management specific
  user: {
    view: { show: commonConditions.always },
    edit: { show: commonConditions.activeOnly },
    delete: { show: createCustomCondition([
      commonConditions.nonSystemGenerated,
      commonConditions.inactiveOnly
    ], 'AND') }
  }
};

// Export default for easy importing
export default {
  createShowFunction,
  commonConditions,
  createActionButtons,
  createProtectedActionButtons,
  createCustomCondition,
  presetConfigurations
};
